import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Calendar, CalendarDays, Check, CheckCircle2, ChevronDown,
  Clock3, Download, Dumbbell, FileText, Image, Info, Sparkles, Stethoscope,
} from "lucide-react";
import LandmarkCanvas from "@/components/LandmarkCanvas";
import PinchZoomFrame from "@/components/PinchZoomFrame";
import EvolutionInsights from "@/components/EvolutionInsights";
import PatientLongitudinal, { Comparison } from "@/components/PatientLongitudinal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Disclaimer from "@/components/Disclaimer";
import LoadError from "@/components/LoadError";
import { StudioAnimation } from "@/components/SvgSandbox";
import SagittalAlignmentCard from "@/components/SagittalAlignmentCard";
import PortalThemeToggle from "@/components/PortalThemeToggle";
import { computeSagittal } from "@/lib/sagittal";
import { PORTAL_GRID_COLUMNS } from "@/components/PosturalGrid";

// «Primeiro e último nome» para a saudação.
const greetingName = (name = "") => { const p = name.trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length - 1]}` : p[0] || ""; };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEG_PT = { feet: "Pés", knees: "Joelhos", pelvis: "Bacia", spine: "Coluna (frontal)", spine_lateral: "Curvas laterais", shoulders: "Ombros", cervical: "Cervical", scapula: "Omoplatas" };

const GRID_STORAGE_KEY = "mio:patient-portal-grid";
function readGridPreference() {
  const defaultPreference = { visible: true, intensity: 50 };
  try {
    const stored = window.localStorage.getItem(GRID_STORAGE_KEY);
    if (!stored) return defaultPreference;
    const parsed = JSON.parse(stored);
    const intensity = Number(parsed.intensity);
    return {
      visible: typeof parsed.visible === "boolean" ? parsed.visible : defaultPreference.visible,
      intensity: Number.isFinite(intensity) ? Math.max(0, Math.min(100, intensity)) : defaultPreference.intensity,
    };
  } catch {
    return defaultPreference;
  }
}

function safeMediaUrl(value) {
  if (typeof value !== "string" || !value.trim()) return "";
  if (/^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(value)) return value;
  try {
    const parsed = new URL(value, window.location.origin);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : "";
  } catch {
    return "";
  }
}

function DemonstrationMedia({ imageUrl, videoUrl, title }) {
  const image = safeMediaUrl(imageUrl);
  const video = safeMediaUrl(videoUrl);
  const directVideo = video && /\.(mp4|webm|og[gv])($|[?#])/i.test(video);
  if (!image && !video) return null;

  return (
    <div className="mt-3 space-y-2">
      {directVideo ? (
        <video
          controls
          playsInline
          preload="none"
          poster={image || undefined}
          aria-label={`Demonstração: ${title}`}
          className="w-full rounded-xl border border-slate-700 bg-slate-950"
        >
          <source src={video} />
          Este navegador não consegue reproduzir o vídeo.
        </video>
      ) : image ? (
        <img src={image} alt={`Demonstração de ${title}`} loading="lazy" className="max-h-72 w-full rounded-xl border border-slate-700 bg-slate-950 object-contain" />
      ) : null}
      {video && !directVideo && (
        <a
          href={video}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-10 items-center rounded-full border border-sky-400/25 px-3 text-xs font-medium text-sky-200 hover:bg-sky-400/10"
        >
          Abrir demonstração em vídeo
        </a>
      )}
    </div>
  );
}

export default function PortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [netErr, setNetErr] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [activePhotoView, setActivePhotoView] = useState("front");
  const [gridPreference, setGridPreference] = useState(readGridPreference);
  const [compareOpen, setCompareOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [evolutionOpen, setEvolutionOpen] = useState(false);

  const load = useCallback(async () => {
    setErr(null); setNetErr(false);
    try {
      const r = await axios.get(`${API}/portal/${token}`, { timeout: 20000 });
      setData(r.data);
      setActivePhotoView(r.data?.assessment?.front_image ? "front" : r.data?.assessment?.side_image ? "side" : "back");
    } catch (e) {
      const st = e.response?.status;
      if (!st) { setNetErr(true); return; }
      setErr(st === 404 ? "Este link não é válido ou já expirou."
        : st === 410 ? (e.response?.data?.detail || "Este link foi desativado pelo profissional.")
        : "Falha a carregar o portal.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    try {
      window.localStorage.setItem(GRID_STORAGE_KEY, JSON.stringify(gridPreference));
    } catch {
      // The grid preference remains active for this page if storage is unavailable.
    }
  }, [gridPreference]);

  const confirm = async (appt_id, action) => {
    if (confirming) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/portal/${token}/appointments/${appt_id}/confirm`, { action }, { timeout: 20000 });
      toast.success(action === "confirm" ? "Presença confirmada" : "Pedido de reagendamento enviado");
      load();
    } catch { toast.error("Não foi possível registar a resposta"); }
    finally { setConfirming(false); }
  };

  if (netErr && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadError testId="portal-network-error" message="Não foi possível abrir o relatório. Verifique a ligação à internet e tente novamente." onRetry={load} />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="portal-error">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center max-w-md">
          <Activity className="w-10 h-10 mx-auto text-sky-400" strokeWidth={1.5} />
          <div className="font-display text-xl text-slate-50 mt-4">Link indisponível</div>
          <div className="text-slate-400 text-sm mt-2">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="portal-loading">A carregar…</div>;

  const { patient, assessment, global_score, segments, patterns, plumb_line, patient_friendly, techniques, clinical_phases, home_plan, appointments, pdf_url, chains, consequences, visceral_insights, evolution } = data;
  const photoViews = [
    { key: "front", title: "Frontal", image: assessment.front_image, landmarks: assessment.landmarks_front },
    { key: "side", title: "Lateral", image: assessment.side_image, landmarks: assessment.landmarks_side },
    { key: "back", title: "Posterior", image: assessment.back_image, landmarks: assessment.landmarks_back },
  ].filter((view) => view.image);
  const selectedPhotoView = photoViews.find((view) => view.key === activePhotoView) || photoViews[0];
  const hasHistory = (data.longitudinal || []).length > 1;
  const sagittal = computeSagittal(assessment.landmarks_side, assessment.sagittal_orientation);
  const summaryLong = (patient_friendly || "").length > 280;
  const byId = (id) => (data.longitudinal || []).find((x) => x.id === id);
  const sessionBefore = data.session_pair && byId(data.session_pair.before_id);
  const sessionAfter = data.session_pair && byId(data.session_pair.after_id);
  // Exercícios: prioritários marcados pelo profissional (estrela); sem marcação, os 3 primeiros do plano.
  const flatExercises = (home_plan || []).flatMap((group, gi) => (group.exercises || []).map((exercise, ei) => ({ exercise, gi, ei, groupName: group.name })));
  const hasStarred = flatExercises.some((x) => x.exercise.priority);
  const priorityExercises = hasStarred ? flatExercises.filter((x) => x.exercise.priority) : flatExercises.slice(0, 3);
  const prioritySet = new Set(priorityExercises.map((x) => `${x.gi}-${x.ei}`));
  const isPriority = (gi, ei) => prioritySet.has(`${gi}-${ei}`);
  const restCount = flatExercises.length - priorityExercises.length;
  const renderExercise = (exercise, groupIndex, exerciseIndex, groupName) => (
    <details key={`${exercise.name}-${groupIndex}-${exerciseIndex}`} className="group rounded-lg border border-slate-800/80 bg-slate-900/55 px-3 py-2.5" data-testid={`portal-exercise-${groupIndex}-${exerciseIndex}`}>
      <summary className="flex cursor-pointer list-none items-start gap-3 [&::-webkit-details-marker]:hidden">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" strokeWidth={1.6} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-slate-100">{exercise.name}</span>
          {groupName && <span className="mt-0.5 block text-[10px] uppercase tracking-wider text-slate-500">{groupName}</span>}
          {exercise.objective && <span className="mt-1 block text-xs leading-relaxed text-slate-400">{exercise.objective}</span>}
          {exercise.precautions?.length > 0 && <span className="mt-1 block text-[11px] leading-snug text-amber-300" data-testid={`portal-exercise-safety-${groupIndex}-${exerciseIndex}`}>Atenção: {exercise.precautions[0]}</span>}
          <span className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            {(exercise.reps || exercise.repetitions) && (
              <span className="rounded-full border border-slate-700 bg-slate-950/50 px-2.5 py-1 text-slate-300">Repetições · {exercise.reps || exercise.repetitions}</span>
            )}
            {exercise.frequency && (
              <span className="rounded-full border border-slate-700 bg-slate-950/50 px-2.5 py-1 text-slate-300">Frequência · {exercise.frequency}</span>
            )}
          </span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open:rotate-180" />
      </summary>
      <div className="ml-7 mt-3 space-y-2 border-t border-slate-800 pt-3 text-xs leading-relaxed text-slate-400">
        {exercise.instructions && <p>{exercise.instructions}</p>}
        {exercise.target_area && <p><span className="text-slate-300">Zona trabalhada: </span>{exercise.target_area}</p>}
        {exercise.demo?.svg && (
          <div className="space-y-1" data-testid={`portal-exercise-demo-${groupIndex}-${exerciseIndex}`}>
            <StudioAnimation svg={exercise.demo.svg} />
            {exercise.demo.caption && <p className="text-[11px] text-slate-500" data-testid={`portal-exercise-demo-caption-${groupIndex}-${exerciseIndex}`}>{exercise.demo.caption}</p>}
          </div>
        )}
        {exercise.precautions?.length > 0 && (
          <p data-testid={`portal-exercise-precautions-${groupIndex}-${exerciseIndex}`}><span className="text-amber-300">Precauções: </span>{exercise.precautions.join(" ")}</p>
        )}
        <DemonstrationMedia imageUrl={exercise.image_url} videoUrl={exercise.video_url} title={exercise.name} />
        {!exercise.instructions && !exercise.target_area && !exercise.image_url && !exercise.video_url && !exercise.demo && (
          <p>A forma de execução deve ser confirmada com o profissional que acompanha o seu plano.</p>
        )}
      </div>
    </details>
  );
  // A preferência da grelha é local ao dispositivo e não altera as definições da avaliação.
  const grid = assessment?.grid_settings || {};
  const showGrid = gridPreference.visible;
  const gridSize = grid.size || 32;
  const gridOpacity = gridPreference.intensity / 100;
  const hasEvolution = Boolean(evolution?.previous && segments);
  const evolutionLabel = hasEvolution ? "Alterações entre registos" : "Ponto de partida";
  const validAppointments = (appointments || [])
    .filter((item) => item.scheduled_at && Number.isFinite(new Date(item.scheduled_at).getTime()))
    .slice()
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const upcomingAppointments = validAppointments.filter((item) => (
    new Date(item.scheduled_at) >= new Date() && item.status !== "completed" && item.status !== "cancelled"
  ));
  const completedAppointments = validAppointments.filter((item) => (
    item.status === "completed" || (new Date(item.scheduled_at) < new Date() && item.status !== "cancelled")
  ));
  const cancelledAppointments = validAppointments.filter((item) => item.status === "cancelled");

  return (
    <div className="portal-page min-h-screen pb-12 text-slate-100" data-testid="portal-page">

      <header className="relative isolate overflow-hidden border-b border-slate-800/80">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,_rgba(56,189,248,0.16),_transparent_50%),radial-gradient(ellipse_at_top_right,_rgba(139,92,246,0.12),_transparent_44%)]" />
        <div className="mx-auto max-w-5xl px-4 pb-3 pt-2.5 sm:px-6 sm:pb-5 sm:pt-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-400/30 bg-sky-400/10">
              <Activity className="h-5 w-5 text-sky-300" strokeWidth={1.6} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-base tracking-[0.22em] text-white">MIO</div>
              <div className="text-[10px] tracking-wide text-slate-500">Mapping Intelligence for Osteopathy</div>
            </div>
            {data.clinic_branding?.clinic_name && (
              <div className="ml-auto hidden max-w-[35%] truncate text-right text-xs text-slate-400 sm:block">
                {data.clinic_branding.clinic_name}
              </div>
            )}
            <PortalThemeToggle className={data.clinic_branding?.clinic_name ? "ml-auto sm:ml-3" : "ml-auto"} />
          </div>

          <div className="mt-2.5 max-w-3xl sm:mt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-300">Avaliação funcional</div>
            <h1 className="mt-0.5 font-display text-xl leading-tight tracking-tight text-white sm:text-3xl">
              Olá, {greetingName(patient.name)}!
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              {assessment.created_at && (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Avaliação de {new Date(assessment.created_at).toLocaleDateString("pt-PT")}
                </span>
              )}
              {data.clinic_branding?.professional_name && (
                <span>Acompanhamento com {data.clinic_branding.professional_name}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-7 px-4 py-5 sm:space-y-9 sm:px-6 sm:py-8">
        {(assessment.front_image || assessment.side_image || assessment.back_image) && (
          <section data-testid="portal-posture-photos" className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Registo visual</div>
                <h2 className="mt-1 font-display text-2xl text-white sm:text-3xl">Fotografias posturais</h2>
                <p className="mt-1 text-sm text-slate-400">Imagens da avaliação, organizadas por perspetiva.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 self-start sm:self-auto">
                <button
                  type="button"
                  data-testid="portal-grid-toggle"
                  onClick={() => setGridPreference((current) => ({ ...current, visible: !current.visible }))}
                  aria-pressed={showGrid}
                  className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-full border px-4 text-xs font-medium transition ${
                    showGrid
                      ? "border-sky-400/40 bg-sky-400/10 text-sky-100"
                      : "border-slate-700 bg-slate-900/70 text-slate-300 hover:border-slate-600"
                  }`}
                >
                  <Image className="h-3.5 w-3.5" /> Grelha {showGrid ? "ativa" : "inativa"}
                </button>
                <label htmlFor="portal-grid-intensity" className="flex min-h-10 items-center gap-2 text-xs text-slate-400">
                  <span>Intensidade</span>
                  <input
                    id="portal-grid-intensity"
                    data-testid="portal-grid-intensity"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={gridPreference.intensity}
                    onChange={(event) => setGridPreference((current) => ({
                      ...current,
                      intensity: Number(event.target.value),
                    }))}
                    aria-valuetext={`${gridPreference.intensity}%`}
                    className="h-10 w-24 cursor-pointer accent-sky-400"
                  />
                  <output htmlFor="portal-grid-intensity" data-testid="portal-grid-intensity-value" className="w-9 text-right font-mono text-slate-300">
                    {gridPreference.intensity}%
                  </output>
                </label>
              </div>
            </div>

            {selectedPhotoView && (
              <Tabs
                value={selectedPhotoView.key}
                onValueChange={setActivePhotoView}
                className="space-y-3"
                aria-label="Perspetiva das fotografias posturais"
              >
                <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl border border-slate-800 bg-slate-900/70 p-1 sm:inline-flex sm:w-auto">
                  {photoViews.map((view) => (
                    <TabsTrigger
                      key={view.key}
                      value={view.key}
                      data-testid={`portal-photo-tab-${view.key}`}
                      className="min-h-10 rounded-lg px-3 text-xs text-slate-400 data-[state=active]:bg-sky-400/15 data-[state=active]:text-sky-100 sm:min-w-28 sm:px-5"
                    >
                      {view.title}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {photoViews.map((view) => (
                  <TabsContent key={view.key} value={view.key} className="mt-0">
                    <figure className="mx-auto max-w-md overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/55 p-2.5 sm:max-w-lg">
                      <PinchZoomFrame>
                        <LandmarkCanvas
                          image={view.image}
                          landmarks={view.landmarks}
                          view={view.key}
                          showSkeleton
                          showSpine={view.key !== "front"}
                          showPlumb={view.key === "side"}
                          interactive={false}
                          segments={segments}
                          plumbLine={view.key === "side" ? plumb_line : undefined}
                          sagittal={view.key === "side" ? sagittal : undefined}
                          showGrid={showGrid}
                          gridColumns={PORTAL_GRID_COLUMNS}
                          gridSize={gridSize}
                          gridOpacity={gridOpacity}
                          onChange={() => {}}
                        />
                      </PinchZoomFrame>
                      <figcaption className="px-2 pb-1 pt-3 text-sm font-medium text-slate-200">Vista {view.title.toLowerCase()}</figcaption>
                    </figure>
                  </TabsContent>
                ))}
              </Tabs>
            )}
            <p className="text-xs leading-relaxed text-slate-500">
              As imagens apoiam a conversa sobre o movimento e são interpretadas pelo profissional no contexto da avaliação.
            </p>
            {sessionBefore && sessionAfter && (
              <details className="group/cmp rounded-2xl border border-sky-400/25 bg-slate-900/55 p-3 sm:p-4" data-testid="portal-session-comparison" onToggle={(e) => setCompareOpen(e.currentTarget.open)}>
                <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-sky-100 [&::-webkit-details-marker]:hidden" data-testid="portal-session-comparison-toggle">
                  Comparar antes/depois do tratamento
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-400 transition group-open/cmp:rotate-180" />
                </summary>
                {compareOpen && (
                  <div className="mt-3 space-y-4">
                    <p className="text-xs leading-relaxed text-slate-400" data-testid="portal-session-caveat">Fotografias feitas na mesma consulta. Pequenas diferenças podem dever-se à posição ou à fotografia; o significado é explicado pelo profissional.</p>
                    <Comparison
                      before={sessionBefore}
                      after={sessionAfter}
                      labels={["Antes do tratamento", "Depois do tratamento"]}
                      title="Antes e depois do tratamento"
                      testId="portal-session-compare"
                      gridVisible={gridPreference.visible}
                      onGridVisibleChange={(visible) => setGridPreference((current) => ({ ...current, visible }))}
                      gridSize={32}
                      gridOpacity={gridPreference.intensity}
                      onGridOpacityChange={(intensity) => setGridPreference((current) => ({ ...current, intensity }))}
                    />
                  </div>
                )}
              </details>
            )}
          </section>
        )}

        <section className="space-y-4" data-testid="portal-observed">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">O que observámos</div>
            <h2 className="mt-1 font-display text-2xl text-white sm:text-3xl">Resumo da avaliação</h2>
          </div>
        <section className="rounded-2xl border border-slate-800 bg-slate-900/65 p-5 shadow-xl shadow-black/10 sm:p-7" aria-labelledby="portal-summary-heading">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-300">
              <Info className="h-4 w-4" />
            </div>
            <div>
              <h2 id="portal-summary-heading" className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300">Em resumo</h2>
              <p className={`mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-200 sm:text-base ${summaryLong && !summaryOpen ? "line-clamp-4" : ""}`} data-testid="portal-summary-text">
                {patient_friendly || "Principais pontos do seu acompanhamento e do plano definido com o profissional."}
              </p>
              {(summaryLong || data.summary_review) && (
                <button type="button" onClick={() => setSummaryOpen((v) => !v)} aria-expanded={summaryOpen} data-testid="portal-summary-more"
                  className="mt-2 inline-flex min-h-10 items-center gap-1.5 text-sm font-medium text-sky-300 hover:text-sky-200">
                  {summaryOpen ? "Mostrar menos" : "Ler avaliação completa"} <ChevronDown className={`h-4 w-4 transition ${summaryOpen ? "rotate-180" : ""}`} />
                </button>
              )}
              {summaryOpen && data.summary_review && (
                <dl className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="portal-summary-review">
                  {[["observations", "O que se observou nas fotografias"], ["symptoms", "O que nos referiu"], ["hypotheses", "Interpretação prudente do profissional"]].map(([k, label]) => (
                    data.summary_review[k] ? (
                      <div key={k} className="rounded-xl border border-slate-700/70 bg-slate-950/40 p-3" data-testid={`portal-summary-${k}`}>
                        <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</dt>
                        <dd className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-slate-200">{data.summary_review[k]}</dd>
                      </div>
                    ) : null
                  ))}
                </dl>
              )}
              {patterns?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {patterns.map((pattern, index) => (
                    <span key={`${pattern.name}-${index}`} className="rounded-full border border-slate-700 bg-slate-950/60 px-3 py-1 text-xs text-slate-300">
                      {pattern.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
        {(segments || chains?.length > 0 || consequences?.length > 0 || visceral_insights?.length > 0 || (assessment.side_image && sagittal.available)) && (
          <details className="group rounded-2xl border border-slate-800 bg-slate-900/35 p-5 sm:p-6" data-testid="portal-assessment-details">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-slate-300 [&::-webkit-details-marker]:hidden">
              <span className="min-w-0">
                <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" /> Mais detalhes da avaliação</span>
                <span className="mt-0.5 block text-xs font-normal text-slate-500">Observações fotográficas, alinhamento visto de lado e explicações técnicas.</span>
              </span>
              <ChevronDown className="h-4 w-4 text-slate-500 transition group-open:rotate-180" />
            </summary>
            <div className="mt-5 space-y-6 border-t border-slate-800 pt-5">
              {assessment.side_image && sagittal.available && <SagittalAlignmentCard result={sagittal} variant="patient" />}
              {segments && (
                <div>
                  <h3 className="text-sm font-medium text-white">Áreas observadas</h3>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {Object.entries(segments).map(([key, segment]) => {
                      const tone = segment.severity === "normal"
                        ? "border-sky-500/20 bg-sky-500/[0.04]"
                        : segment.severity === "moderado"
                          ? "border-amber-500/20 bg-amber-500/[0.04]"
                          : "border-slate-700 bg-slate-950/40";
                      return (
                        <div key={key} className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${tone}`}>
                          <span className="text-sm text-slate-200">{SEG_PT[key] || key}</span>
                          <span className="text-right text-xs text-slate-400">
                            {segment.severity && <span className="capitalize">{segment.severity}</span>}
                            {segment.score != null && <span className="ml-2 text-slate-500">· {segment.score}</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {chains?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white">Observações funcionais</h3>
                  <div className="mt-2 space-y-2">
                    {chains.map((chain, index) => (
                      <div key={`${chain.name}-${index}`} className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                        <div className="text-sm text-slate-200">{chain.name}{chain.status ? <span className="text-xs text-slate-500"> · {chain.status}</span> : null}</div>
                        {chain.description && <p className="mt-1 text-xs leading-relaxed text-slate-400">{chain.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {consequences?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white">Contexto do acompanhamento</h3>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm leading-relaxed text-slate-400">
                    {consequences.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                  </ul>
                </div>
              )}
              {visceral_insights?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-white">Outras observações para revisão profissional</h3>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm leading-relaxed text-slate-400">
                    {visceral_insights.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
                  </ul>
                </div>
              )}
              {global_score != null && (
                <p className="text-xs text-slate-500">
                  Indicadores e observações são complementares e devem ser interpretados pelo profissional no contexto da avaliação.
                </p>
              )}
            </div>
          </details>
        )}
        </section>

        <section className="space-y-4" data-testid="portal-follow-up-plan">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">O meu plano</div>
            <h2 className="mt-1 font-display text-2xl text-white sm:text-3xl">Plano de acompanhamento</h2>
            <p className="mt-1 text-sm text-slate-400">O plano é personalizado e revisto com o profissional ao longo do acompanhamento.</p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="rounded-2xl border border-slate-800 bg-slate-900/55 p-5 sm:p-6" data-testid="portal-phases">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-400/10 text-sky-200">
                  <Stethoscope className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-white">Técnicas em consulta</h3>
                  <p className="text-xs text-slate-500">Abordagens a rever com o profissional</p>
                </div>
              </div>

              {clinical_phases?.some((phase) => (phase.techniques || []).length > 0) ? (
                <div className="mt-5 space-y-3">
                  {clinical_phases.map((phase, phaseIndex) => {
                    const phaseTechniques = phase.techniques || [];
                    if (!phaseTechniques.length) return null;
                    return (
                      <div key={`${phase.phase}-${phaseIndex}`} className="rounded-xl border border-slate-800 bg-slate-950/45 p-4">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-300">Fase {phase.phase}</div>
                        <h4 className="mt-1 font-medium text-slate-100">{(phase.name || "").replace(/^Fase \d+ — /, "")}</h4>
                        {phase.goal && (
                          <details className="group mt-2">
                            <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs text-slate-500 [&::-webkit-details-marker]:hidden">
                              Sobre esta fase <ChevronDown className="h-3.5 w-3.5 transition group-open:rotate-180" />
                            </summary>
                            <p className="mt-2 text-xs leading-relaxed text-slate-400">{phase.goal}</p>
                          </details>
                        )}
                        <div className="mt-3 space-y-2">
                          {phaseTechniques.map((technique, techniqueIndex) => (
                            <details key={`${technique.name}-${techniqueIndex}`} className="group rounded-lg border border-slate-800/80 bg-slate-900/55 px-3 py-2.5">
                              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm text-slate-100 [&::-webkit-details-marker]:hidden">
                                <span className="min-w-0">
                                  <span className="block font-medium">{technique.name}</span>
                                  {technique.target_area && <span className="mt-0.5 block text-xs text-slate-500">{technique.target_area}</span>}
                                </span>
                                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open:rotate-180" />
                              </summary>
                              <div className="space-y-2 border-t border-slate-800 pt-3 mt-3 text-xs leading-relaxed text-slate-400">
                                {technique.description && <p>{technique.description}</p>}
                                {technique.objective && <p><span className="text-slate-300">Objetivo funcional: </span>{technique.objective}</p>}
                                {technique.indication && <p><span className="text-slate-300">Nota: </span>{technique.indication}</p>}
                                {technique.instructions && <p>{technique.instructions}</p>}
                                {!technique.description && !technique.objective && !technique.indication && !technique.instructions && (
                                  <p>Os detalhes serão revistos com o profissional em consulta.</p>
                                )}
                                <DemonstrationMedia imageUrl={technique.image_url} videoUrl={technique.video_url} title={technique.name} />
                              </div>
                            </details>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : techniques?.length > 0 ? (
                <div className="mt-5 space-y-2">
                  {techniques.map((technique, index) => (
                    <details key={`${technique.name}-${index}`} className="group rounded-xl border border-slate-800 bg-slate-950/45 p-4">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-slate-100 [&::-webkit-details-marker]:hidden">
                        <span>{technique.name}</span><ChevronDown className="h-4 w-4 text-slate-500 transition group-open:rotate-180" />
                      </summary>
                      <div className="mt-3 space-y-2 border-t border-slate-800 pt-3 text-xs leading-relaxed text-slate-400">
                        {technique.target_area && <p>Área: {technique.target_area}</p>}
                        {technique.objective && <p>Objetivo funcional: {technique.objective}</p>}
                        {technique.description && <p>{technique.description}</p>}
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <p className="mt-5 rounded-xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">
                  As técnicas serão revistas com o profissional durante as sessões.
                </p>
              )}
            </section>

            <section className="rounded-2xl border border-slate-800 bg-slate-900/55 p-5 sm:p-6" data-testid="portal-home-plan">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-200">
                  <Dumbbell className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-white">Exercícios entre sessões</h3>
                  <p className="text-xs text-slate-500">Complemento ao acompanhamento em consulta</p>
                </div>
              </div>

              {home_plan?.some((group) => (group.exercises || []).length > 0) ? (
                <div className="mt-5 space-y-3">
                  <div className="rounded-xl border border-amber-400/25 bg-slate-950/45 p-4" data-testid="portal-home-priority">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-300">{hasStarred ? "Prioritários (indicados pelo profissional)" : "Para começar"}</div>
                    <div className="mt-3 space-y-2">
                      {priorityExercises.map(({ exercise, gi, ei, groupName }) => renderExercise(exercise, gi, ei, groupName))}
                    </div>
                  </div>
                  {restCount > 0 && (
                    <details className="group/rest rounded-xl border border-slate-800 bg-slate-950/30 p-3" data-testid="portal-home-rest">
                      <summary className="flex min-h-10 cursor-pointer list-none items-center justify-between gap-3 text-sm text-slate-300 [&::-webkit-details-marker]:hidden" data-testid="portal-home-rest-toggle">
                        Ver os restantes {restCount} exercícios, por objetivo
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open/rest:rotate-180" />
                      </summary>
                      <div className="mt-3 space-y-3">
                        {home_plan.map((group, groupIndex) => {
                          const rest = (group.exercises || []).map((exercise, exerciseIndex) => ({ exercise, exerciseIndex })).filter(({ exerciseIndex }) => !isPriority(groupIndex, exerciseIndex));
                          return rest.length > 0 && (
                            <div key={`${group.group}-${groupIndex}`} className="rounded-xl border border-slate-800 bg-slate-950/45 p-4" data-testid={`portal-home-${group.group}`}>
                              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">{group.name}</div>
                              <div className="mt-3 space-y-2">{rest.map(({ exercise, exerciseIndex }) => renderExercise(exercise, groupIndex, exerciseIndex))}</div>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </div>
              ) : (
                <p className="mt-5 rounded-xl border border-dashed border-slate-800 p-4 text-sm text-slate-500">
                  Não há exercícios definidos para esta avaliação.
                </p>
              )}
            </section>
          </div>
        </section>

        <section data-testid="portal-appointments" className="space-y-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Agenda</div>
            <h2 className="mt-1 font-display text-2xl text-white sm:text-3xl">Próximas sessões</h2>
            <p className="mt-1 text-sm text-slate-400">Marcações previstas e confirmação de presença.</p>
          </div>

          {upcomingAppointments.length > 0 ? (
            <div className="space-y-3">
              {upcomingAppointments.map((appointment) => {
                const confirmed = appointment.patient_confirmation === "confirmed";
                const rescheduleRequested = appointment.patient_confirmation === "cancel_requested";
                return (
                  <article key={appointment.id} data-testid={`portal-appt-${appointment.id}`} className="rounded-2xl border border-sky-400/20 bg-slate-900/65 p-4 sm:p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-400/10 text-sky-200">
                          <Calendar className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-medium text-white">
                            {new Date(appointment.scheduled_at).toLocaleString("pt-PT", { dateStyle: "full", timeStyle: "short" })}
                          </h3>
                          <p className="mt-1 text-xs text-slate-400">
                            Sessão {appointment.session_number || "—"}{appointment.notes ? ` · ${appointment.notes}` : ""}
                          </p>
                        </div>
                      </div>
                      {confirmed ? (
                        <span className="inline-flex min-h-10 items-center gap-2 self-start rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 text-sm text-emerald-200 sm:self-auto">
                          <CheckCircle2 className="h-4 w-4" /> Presença confirmada
                        </span>
                      ) : rescheduleRequested ? (
                        <span className="inline-flex min-h-10 items-center self-start rounded-full border border-amber-400/25 bg-amber-400/10 px-4 text-sm text-amber-200 sm:self-auto">
                          Pedido de reagendamento enviado
                        </span>
                      ) : (
                        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                          <Button
                            disabled={confirming}
                            onClick={() => confirm(appointment.id, "confirm")}
                            data-testid={`confirm-${appointment.id}`}
                            className="min-h-10 rounded-full bg-emerald-400 px-4 text-slate-950 hover:bg-emerald-300 disabled:opacity-50"
                          >
                            <Check className="mr-2 h-4 w-4" /> Confirmar presença
                          </Button>
                          <Button
                            disabled={confirming}
                            onClick={() => confirm(appointment.id, "reschedule")}
                            data-testid={`reschedule-${appointment.id}`}
                            variant="outline"
                            className="min-h-10 rounded-full border-slate-700 px-4 text-slate-200 hover:bg-slate-800"
                          >
                            Pedir reagendamento
                          </Button>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900/30 p-5 text-sm text-slate-400">
              Não existem sessões futuras marcadas neste plano.
            </div>
          )}

          {completedAppointments.length > 0 && (
            <details className="group rounded-2xl border border-slate-800 bg-slate-900/35 p-4 sm:p-5" data-testid="portal-completed-appointments">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-slate-300 [&::-webkit-details-marker]:hidden">
                <span className="inline-flex items-center gap-2"><Clock3 className="h-4 w-4 text-slate-500" /> Sessões realizadas ({completedAppointments.length})</span>
                <ChevronDown className="h-4 w-4 text-slate-500 transition group-open:rotate-180" />
              </summary>
              <div className="mt-3 space-y-2 border-t border-slate-800 pt-3">
                {completedAppointments.map((appointment) => (
                  <div key={appointment.id} data-testid={`portal-appt-${appointment.id}`} className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-slate-300">{new Date(appointment.scheduled_at).toLocaleString("pt-PT", { dateStyle: "medium", timeStyle: "short" })}</span>
                    <span className="text-xs text-slate-500">Sessão {appointment.session_number || "—"} · Realizada</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {cancelledAppointments.length > 0 && (
            <details className="group rounded-2xl border border-slate-800 bg-slate-900/30 p-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm text-slate-400 [&::-webkit-details-marker]:hidden">
                Marcações canceladas ({cancelledAppointments.length})
                <ChevronDown className="h-4 w-4 text-slate-500 transition group-open:rotate-180" />
              </summary>
              <div className="mt-3 space-y-2 border-t border-slate-800 pt-3">
                {cancelledAppointments.map((appointment) => (
                  <div key={appointment.id} data-testid={`portal-appt-${appointment.id}`} className="text-sm text-slate-500">
                    {new Date(appointment.scheduled_at).toLocaleString("pt-PT", { dateStyle: "medium", timeStyle: "short" })} · Sessão {appointment.session_number || "—"}
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>

        <details className="group/evo rounded-2xl border border-slate-800 bg-slate-900/40 p-4 sm:p-5" data-testid="portal-evolution-section" onToggle={(e) => setEvolutionOpen(e.currentTarget.open)}>
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden" data-testid="portal-evolution-toggle">
            <span><span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">A minha evolução</span><span className="mt-1 block font-display text-xl text-white">Evolução funcional</span></span>
            <ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition group-open/evo:rotate-180" />
          </summary>
          {evolutionOpen && (
            <div className="mt-4 space-y-5">
        <section data-testid="portal-evolution" className="rounded-2xl border border-sky-400/20 bg-gradient-to-br from-sky-400/[0.09] via-slate-900/80 to-emerald-400/[0.05] p-5 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-sky-300/20 bg-sky-300/10">
              <Activity className="h-5 w-5 text-sky-200" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-200">Evolução funcional</div>
              <h2 className="mt-1 font-display text-xl text-white sm:text-2xl">{evolutionLabel}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                {hasEvolution
                  ? `Comparação com o registo de ${new Date(evolution.previous.created_at).toLocaleDateString("pt-PT")}. As diferenças numéricas não determinam, por si só, melhoria clínica.`
                  : "Esta avaliação constitui o ponto de partida para acompanhar o seu percurso funcional."}
              </p>
            </div>
          </div>
          {hasEvolution && (
            <details className="group mt-5 border-t border-slate-700/60 pt-4" data-testid="portal-evolution-details">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-slate-300 marker:hidden [&::-webkit-details-marker]:hidden">
                Ver indicadores comparados
                <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 transition group-open:rotate-180" />
              </summary>
              <div className="mt-4">
                <EvolutionInsights
                  assessment={{
                    id: assessment.id,
                    patient_id: patient?.id,
                    analysis: { global_score, segments, patterns: patterns || [] },
                  }}
                  previousOverride={evolution.previous}
                  historyOverride={evolution.history || []}
                  hideDeepLink
                  neutralLanguage
                />
              </div>
            </details>
          )}
        </section>
        {hasHistory && (
          <PatientLongitudinal
            assessments={data.longitudinal || []}
            gridPreference={gridPreference}
            onGridPreferenceChange={setGridPreference}
          />
        )}
        {!hasHistory && (
          <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-4 sm:p-5" data-testid="portal-longitudinal-unavailable">
            <h2 className="text-sm font-medium text-slate-200">Histórico de avaliações</h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-400">
              Aqui aparecem apenas as avaliações anteriores que o profissional partilhou individualmente consigo. De momento, só existe esta avaliação partilhada.
            </p>
          </section>
        )}
            </div>
          )}
        </details>

        <footer className="space-y-5 border-t border-slate-800/80 pt-6 text-center sm:pt-8">
          {pdf_url ? (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}${pdf_url}`}
              target="_blank"
              rel="noreferrer"
              data-testid="portal-download-pdf"
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 text-xs font-medium text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              <Download className="h-4 w-4" /> Consultar versão PDF
            </a>
          ) : null}
          <Disclaimer compact className="mx-auto max-w-2xl" />
          <p className="text-[11px] text-slate-600">Este portal é pessoal. Por favor, não partilhe o link com outras pessoas.</p>
        </footer>
      </main>
    </div>
  );
}
